import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TAvailableLanguageCodes, TFormSubType } from '@hubertai/screening-question-types';
import type { TRuleEditorCommonProps } from '../types';
import HubertInput from 'components/HubertUI/HubertInput';
import { HubertSlider } from 'components/HubertUI/HubertSlider';
import HubertSwitch from 'components/HubertUI/HubertSwitch';
import type { TQuestionRuleCondition } from 'types/rules.types';
import { StripHTML } from '@/utils/utils';

interface Props extends TRuleEditorCommonProps {
  condition: TQuestionRuleCondition;
  language?: TAvailableLanguageCodes;
  forms?: TFormSubType[];
  onChange: (patch: Partial<TQuestionRuleCondition>) => void;
}

const RuleConditionIfAnswerForm = ({ condition, isReadOnly, language, forms, onChange }: Props) => {
  const { t } = useTranslation('Templates');

  const committedSliderValue =
    Array.isArray(condition.value) && condition.value.length ? Number(condition.value[0]) : 0;
  const [sliderInput, setSliderInput] = useState(committedSliderValue);
  const [lastCommittedSliderValue, setLastCommittedSliderValue] = useState(committedSliderValue);
  if (committedSliderValue !== lastCommittedSliderValue) {
    setLastCommittedSliderValue(committedSliderValue);
    setSliderInput(committedSliderValue);
  }

  if (!language || !forms) {
    return <div>{t('question.ruleConditions.notFormTypeQuestion')}</div>;
  }

  const targetForm = forms.find((form) => form.id === condition.apply_to_form_id);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex">
        {t('question.ruleConditions.applyToFormElement')}
        {forms.map((form) => (
          <div key={form.id}>
            {form.label?.[language] ? StripHTML(form.label[language]) : ''}
            <HubertSwitch
              disabled={isReadOnly}
              checked={condition.apply_to_form_id === form.id}
              onCheckedChange={(checked) => onChange({ apply_to_form_id: checked ? form.id : undefined })}
            />
          </div>
        ))}
      </div>

      {targetForm?.type === 'slider' ? (
        condition.operator !== 'BETWEEN' ? (
          <HubertInput
            disabled={isReadOnly}
            type="number"
            value={sliderInput}
            min={targetForm.min?.[language] ?? 0}
            max={targetForm.max?.[language] ?? 100}
            step={targetForm.step?.[language] ?? 1}
            onChange={(e) => setSliderInput(Number(e.target.value))}
            onBlur={(e) => onChange({ value: [Number(e.target.value)] })}
          />
        ) : (
          <div className="flex flex-row justify-between gap-2">
            <div>{Array.isArray(condition.value) ? condition.value[0] : 0}</div>
            <HubertSlider
              disabled={isReadOnly}
              value={
                Array.isArray(condition.value) && condition.value.length === 2
                  ? (condition.value as unknown as number[])
                  : [0, 0]
              }
              min={targetForm.min?.[language] ?? 0}
              max={targetForm.max?.[language] ?? 100}
              step={targetForm.step?.[language] ?? 1}
              onValueChange={(value) => onChange({ value: value as unknown as string[] })}
            />
            <div>{Array.isArray(condition.value) ? condition.value[1] : 0}</div>
          </div>
        )
      ) : null}
    </div>
  );
};

export default RuleConditionIfAnswerForm;
